import { Buffer } from "buffer";
import { AnchorProvider, Program } from "@coral-xyz/anchor";
import bs58 from "bs58";
import { Keypair, PublicKey } from "@solana/web3.js";
import { sha256 } from "js-sha256";
import { generatePDA } from "./pda";
import { uuidBs58 } from "./uuid";
import { Fartlab } from "idl/types/fartlab";
import { BN } from "bn.js";

// PDA seed for votes
const VOTE_PDA_SEED = "vote";

/**
 * Fetches a single vote by UUID and voter pubkey
 * The uuid is the submission one, not the vote
 */
export async function getVote(
  program: Program<Fartlab>,
  uuid: string,
  voter: PublicKey
) {
  const votePDA = await generatePDA(program, VOTE_PDA_SEED, uuid, voter);
  console.log(`🔍 Fetching Vote Submission at PDA: ${new PublicKey(votePDA).toBase58()}`);

  const vote = await program.account.vote.fetch(votePDA);
  return vote;
}


/**
 * Fetches all votes from the blockchain
 */
export async function fetchAllVotes(program: Program<Fartlab>, provider: AnchorProvider) {
  const programId = program.programId;

  // ✅ Get the discriminator for "Vote"
  const discriminator = bs58.encode(Buffer.from(sha256.digest("account:Vote")).subarray(0, 8));

  const accounts = await provider.connection.getProgramAccounts(programId, {
    filters: [
      {
        memcmp: {
          offset: 0,
          bytes: discriminator,
        },
      },
    ],
  });

  const votes = accounts.map((acc) => program.coder.accounts.decode("vote", acc.account.data));
  return votes;
}

/**
 * Fetches all votes linked to a specific fart submission
 */
export async function fetchVotesForSubmission(
  program: Program<Fartlab>,
  provider: AnchorProvider,
  fartSubmissionUuid?: string,
  voterPublicKey?: string
) {
  console.log(`🔍 Fetching Votes${fartSubmissionUuid ? ` for Submission: ${fartSubmissionUuid}` : ""}`);

  // ✅ Fetch all votes
  const allVotes = await fetchAllVotes(program, provider);

  // ✅ Apply filters if provided
  const filteredVotes = allVotes.filter((vote) => {
    const matchSubmission = fartSubmissionUuid ? vote.fartSubmissionUuid === fartSubmissionUuid : true;
    const matchVoter = voterPublicKey ? vote.voter === voterPublicKey : true;
    return matchSubmission && matchVoter;
  });

  console.log(`✅ Found ${filteredVotes.length} votes with filters`);
  return filteredVotes;
}


/**
 * Creates a new vote
 */
export async function createVote(program: Program<Fartlab>, payer: Keypair, voteData: any, paymentTx: string) {
  const submittedAt = voteData.submittedAt || new BN(Math.floor(Date.now() / 1000)).add(new BN(100));
  const uuid = voteData.uuid || uuidBs58();
  const championshipUuid = voteData.championshipUuid;
  const fartSubmissionUuid = voteData.fartSubmissionUuid;
  const voter = voteData.voter;
  try {
    const tx = await program.methods
      .createVote(uuid, championshipUuid, fartSubmissionUuid, voter, submittedAt, paymentTx)
      .accounts({
        owner: payer.publicKey,
      })
      .signers([payer])
      .rpc();
    // console.log(`✅ Vote created ${uuid} for submission ${fartSubmissionUuid} and championship ${championshipUuid} ! Transaction:`, tx);

    return { success: true, tx };
  } catch (error) {
    console.error("❌ Failed to create Vote:", error.errorCode);
    return {
      success: false,
      error: {
        message: error?.error?.errorMessage || "Unknown error occurred.",
        code: error?.error?.errorCode?.number || null,
        logs: error?.logs || [],
      },
    };
  }
}

/**
 * Updates a vote (Only voter can update)
 */
export async function updateVote(program: Program<Fartlab>, owner: Keypair, voter: PublicKey, fartSubmissionUuid: string, newPaymentTx?: string) {
  try {
    const votePDA = await generatePDA(program, VOTE_PDA_SEED, fartSubmissionUuid, voter);
    console.log(`🔍 Updating Vote at PDA: ${new PublicKey(votePDA).toBase58()}`);

    const tx = await program.methods
      .updateVote(newPaymentTx || null)
      .accounts({
        vote: votePDA,
        owner: owner.publicKey,
      })
      .signers([owner])
      .rpc();

    console.log(`🗳️ Vote updated! Transaction: ${tx}`);
    return tx;
  } catch (error) {
    console.error(`❌ Failed to update Vote:`);
    throw error;
  }
}

/**
 * Deletes a vote
 */
export async function deleteVote(program: Program<Fartlab>, owner: Keypair, voter: PublicKey, fartSubmissionUuid: string) {
  try {
    const votePDA = await generatePDA(program, VOTE_PDA_SEED, fartSubmissionUuid, voter);
    // console.log(`🔍 Deleting Vote at PDA: ${votePDA.toBase58()}`);
    const tx = await program.methods
      .deleteVote()
      .accounts({
        vote: votePDA,
        owner: owner.publicKey,
      })
      .signers([owner])
      .rpc();

    // console.log(`🗑️ Vote deleted! Transaction: ${tx}`);
    return tx;
  } catch (error) {
    console.error(`❌ Failed to delete Vote:`, error);
    throw error;
  }
}
